import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import {
  getLscAutomationActivation,
  postLscAutomationActivation,
} from '../../api';
import { lscPath } from '../../routing';
import type { SetupWizardStep } from '../../leagueSetupWizard';
import {
  AUTOMATION_ACTIVATION_ACTIVE_COPY,
  AUTOMATION_ACTIVATION_BUNDLE_TITLE,
  AUTOMATION_ACTIVATION_CHECKLIST_TITLE,
  AUTOMATION_ACTIVATION_COMMAND_CENTER_COPY,
  AUTOMATION_ACTIVATION_ENTITLEMENT_BRIDGE_DISABLED_COPY,
  AUTOMATION_ACTIVATION_INTRO_COPY,
  AUTOMATION_ACTIVATION_PENDING_OPERATOR_COPY,
  AUTOMATION_ACTIVATION_SCHEDULE_BLOCKED_COPY,
  AUTOMATION_ACTIVATION_UNPAID_COPY,
  AUTOMATION_ACTIVATION_ZERO_BROADCASTERS_COPY,
} from '../../setupAutomationCopy';
import type {
  LscAutomationActivationChecklistItem,
  LscAutomationActivationPayload,
  LscAutomationActivationStatus,
} from '../../types';
import LeagueSetupStepCard from '../LeagueSetupStepCard';
import LscMutationErrorAlert from '../LscMutationErrorAlert';
import AutomationActivationArchiveAutopublishPanel from './AutomationActivationArchiveAutopublishPanel';
import { DetectionSwitchControl } from './DetectionSwitchControl';
import { parseLscMutationError } from '../../entitlementBillingErrors';

export interface AutomationActivationStepPanelProps {
  leagueId: string;
  wizardStep: SetupWizardStep;
  isOwner: boolean;
  isOperator: boolean;
  onSetupRefresh: () => void;
}

function checklistTone(status: LscAutomationActivationChecklistItem['status']): string {
  if (status === 'ready') return 'text-emerald-300';
  if (status === 'warning') return 'text-amber-200';
  if (status === 'pending_operator_enablement') return 'text-amber-200';
  if (status === 'locked_unpaid') return 'text-red-200';
  if (status === 'not_applicable') return 'text-secondary-400';
  return 'text-secondary-200';
}

function statusLabel(status: LscAutomationActivationChecklistItem['status']): string {
  switch (status) {
    case 'ready':
      return 'Ready';
    case 'warning':
      return 'Needs attention';
    case 'blocked':
      return 'Blocked';
    case 'pending_operator_enablement':
      return 'Pending rollout';
    case 'entitlement_bridge_disabled':
      return 'Awaiting rollout';
    case 'locked_unpaid':
      return 'Subscription required';
    case 'not_applicable':
      return 'Not applicable';
    default:
      return status;
  }
}

function blockerCopy(
  blockers: string[],
  activationStatus: LscAutomationActivationStatus
): string | null {
  if (activationStatus === 'locked_unpaid' || blockers.includes('billing_inactive')) {
    return AUTOMATION_ACTIVATION_UNPAID_COPY;
  }
  if (
    blockers.some((key) =>
      ['schedule_source_connected', 'current_week_synced', 'active_games', 'games_visible'].includes(key)
    )
  ) {
    return AUTOMATION_ACTIVATION_SCHEDULE_BLOCKED_COPY;
  }
  if (blockers.includes('zero_broadcasters')) {
    return AUTOMATION_ACTIVATION_ZERO_BROADCASTERS_COPY;
  }
  return null;
}

const AutomationActivationStepPanel: React.FC<AutomationActivationStepPanelProps> = ({
  leagueId,
  wizardStep,
  isOwner,
  isOperator,
  onSetupRefresh,
}) => {
  const [activation, setActivation] = useState<LscAutomationActivationPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<ReturnType<typeof parseLscMutationError> | null>(null);

  const commandCenterHref = lscPath(`/leagues/${leagueId}/command-center`);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await getLscAutomationActivation(leagueId);
      setActivation(payload);
    } catch (err) {
      setError(parseLscMutationError(err, 'Unable to load activation readiness.'));
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const blockerMessage = useMemo(() => {
    if (!activation) return null;
    return blockerCopy(activation.blockers ?? [], activation.activation_status);
  }, [activation]);

  const handleActivate = async () => {
    if (!activation?.can_activate) return;
    setActivating(true);
    setError(null);
    try {
      const payload = await postLscAutomationActivation(leagueId);
      setActivation(payload);
      onSetupRefresh();
    } catch (err) {
      setError(parseLscMutationError(err, 'Activation request failed.'));
    } finally {
      setActivating(false);
    }
  };

  if (loading && !activation) {
    return (
      <div className="flex items-center gap-2 text-sm text-secondary-400" data-testid="automation-activation-loading">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading activation readiness…
      </div>
    );
  }

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="automation-activation-step-panel">
      <LeagueSetupStepCard step={wizardStep} />

      {error ? (
        <div data-testid="automation-activation-error">
          <LscMutationErrorAlert error={error} />
        </div>
      ) : null}

      <div className="rounded-lg border border-secondary-700 bg-secondary-900/30 px-4 py-3 text-sm text-secondary-300">
        {AUTOMATION_ACTIVATION_INTRO_COPY}
      </div>

      {blockerMessage ? (
        <div
          className="rounded-lg border border-red-700/40 bg-red-950/10 px-4 py-3 text-sm text-red-100 flex gap-2"
          data-testid="automation-activation-blocker"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <p>{blockerMessage}</p>
        </div>
      ) : null}

      {activation?.activation_status === 'pending_operator_enablement' ? (
        <div
          className="rounded-lg border border-amber-700/40 bg-amber-950/10 px-4 py-3 text-sm text-amber-100 flex gap-2"
          data-testid="automation-activation-pending-operator"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <p>{AUTOMATION_ACTIVATION_PENDING_OPERATOR_COPY}</p>
        </div>
      ) : null}

      {activation?.activation_status === 'entitlement_bridge_disabled' ? (
        <div
          className="rounded-lg border border-amber-700/40 bg-amber-950/10 px-4 py-3 text-sm text-amber-100 flex gap-2"
          data-testid="automation-activation-entitlement-bridge-disabled"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <p>{AUTOMATION_ACTIVATION_ENTITLEMENT_BRIDGE_DISABLED_COPY}</p>
        </div>
      ) : null}

      {activation?.activation_status === 'active' ? (
        <div
          className="rounded-lg border border-emerald-700/40 bg-emerald-950/10 px-4 py-3 text-sm text-emerald-100 flex gap-2"
          data-testid="automation-activation-active"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <p>{AUTOMATION_ACTIVATION_ACTIVE_COPY}</p>
        </div>
      ) : null}

      <DetectionSwitchControl
        leagueId={leagueId}
        canEdit={isOperator}
        onChanged={onSetupRefresh}
      />

      <div className="card space-y-3" data-testid="automation-activation-checklist">
        <h4 className="text-sm font-semibold text-white">{AUTOMATION_ACTIVATION_CHECKLIST_TITLE}</h4>
        <ul className="space-y-2">
          {(activation?.checklist ?? []).map((item) => (
            <li
              key={item.key}
              className="flex items-start justify-between gap-3 text-sm"
              data-testid={`automation-activation-check-${item.key}`}
            >
              <div>
                <p className="font-medium text-secondary-100">{item.title}</p>
                <p className="text-secondary-400 text-xs mt-0.5">{item.description}</p>
              </div>
              <span className={`text-xs font-medium shrink-0 ${checklistTone(item.status)}`}>
                {statusLabel(item.status)}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="card space-y-3" data-testid="automation-activation-bundle">
        <h4 className="text-sm font-semibold text-white">{AUTOMATION_ACTIVATION_BUNDLE_TITLE}</h4>
        <ul className="space-y-2">
          {(activation?.bundle_lanes ?? []).map((lane) => (
            <li
              key={lane.lane_key}
              className="flex items-start justify-between gap-3 text-sm"
              data-testid={`automation-activation-lane-${lane.lane_key}`}
            >
              <div>
                <p className="font-medium text-secondary-100">{lane.label}</p>
                <p className="text-secondary-400 text-xs mt-0.5">{lane.description}</p>
              </div>
              <span className="text-xs text-secondary-400 shrink-0">
                {lane.included_in_bundle ? statusLabel(lane.status) : 'Off'}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <AutomationActivationArchiveAutopublishPanel leagueId={leagueId} />

      <div className="flex flex-wrap items-center gap-3">
        {isOperator ? (
          <button
            type="button"
            className={`btn-primary text-sm min-h-11 px-4 ${activation?.can_activate ? '' : 'opacity-50 cursor-not-allowed'}`}
            onClick={() => void handleActivate()}
            disabled={!activation?.can_activate || activating}
            data-testid={
              activation?.can_activate
                ? 'automation-activation-submit'
                : 'automation-activation-submit-disabled'
            }
          >
            {activating ? 'Activating…' : 'Activate automations'}
          </button>
        ) : (
          <p className="text-sm text-secondary-400" data-testid="automation-activation-member-note">
            Ask a commissioner to activate automations for this league.
          </p>
        )}

        <Link
          to={commandCenterHref}
          className="btn-secondary text-sm inline-flex min-h-11 items-center px-4"
          data-testid="automation-activation-command-center-link"
        >
          Open Command Center
        </Link>
      </div>

      <p className="text-xs text-secondary-500">{AUTOMATION_ACTIVATION_COMMAND_CENTER_COPY}</p>
    </div>
  );
};

export default AutomationActivationStepPanel;
